import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpFromLine, Plus } from 'lucide-react';
import { EditActionBar } from '../shared/EditActionBar';
import { ExportXlsxButton } from '../shared/ExportXlsxButton';
import type { Driver10Data, JumpRow } from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { fetchExportConductRiskDriverXlsx } from '../../../api/functionApi';

const numCls = 'w-20 text-right bg-white text-[#1B365D] border border-amber-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-amber-500 tabular-nums';
const txtCls = 'w-full min-w-[6rem] bg-white border border-amber-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-amber-500';

/* ─── Helpers ────────────────────────────────────────────── */
const fmtN2 = (n: number) => n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FASCIA_STYLES: Record<string, string> = {
  'NO PREMIO':   'bg-gray-100 text-gray-600 border-gray-200',
  'IV FASCIA':   'bg-red-50 text-red-700 border-red-200',
  'III FASCIA':  'bg-orange-50 text-orange-700 border-orange-200',
  'II FASCIA':   'bg-amber-50 text-amber-700 border-amber-200',
  'I FASCIA':    'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const FasciaBadge = ({ fascia }: { fascia: string }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${FASCIA_STYLES[fascia] ?? FASCIA_STYLES['NO PREMIO']}`}>
    {fascia}
  </span>
);

const JumpBadge = ({ size }: { size: number }) => {
  const label = size === 2 ? 'Doppio' : size === 3 ? 'Triplo' : 'Quadruplo';
  const cls   = size === 4
    ? 'bg-red-100 text-red-800 border-red-300'
    : size === 3
      ? 'bg-orange-100 text-orange-800 border-orange-300'
      : 'bg-amber-100 text-amber-800 border-amber-300';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${cls}`}>
      ↑ {label} (+{size})
    </span>
  );
};

type TabKey = 'double' | 'triple' | 'quadruple';

const emptyJumpRow = (): JumpRow => ({
  branch_name:       '',
  clone_uo:          '',
  area:              '',
  regional_division: '',
  final_score_q3:    0,
  fascia_q3:         '',
  final_score_q4:    0,
  fascia_q4:         '',
  fascia_score_q3:   0,
  fascia_score_q4:   0,
  jump_size:         0,
});

/* ─── Main ───────────────────────────────────────────────── */
const SaltoDiFasceContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver10Data>(reportId, 10);
  const [data,      setData]      = useState<Driver10Data | null>(null);
  const [savedData, setSavedData] = useState<Driver10Data | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [tab,       setTab]       = useState<TabKey>('double');

  useEffect(() => {
    if (!remoteData) return;
    // Mantengo la nullabilita' delle liste: `null` (le tre liste sono null
    // insieme) = analisi non eseguita perche' la fonte 30/09 non e' stata
    // caricata; `[]` = eseguita, nessun salto. Va distinta in UI.
    setData(remoteData);
    setSavedData(remoteData);
  }, [remoteData]);

  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setIsEditing(false);
  };

  /** Aggiorna un campo NUMERICO di una riga (identificata da clone_uo) in TUTTE
   *  le 3 tabelle in cui la filiale eventualmente compare. Editing libero:
   *  nessun ricalcolo lato client. */
  const updateRow = (cloneUo: string, key: keyof JumpRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => {
      if (!prev) return prev;
      const patch = (rows: JumpRow[] | null) => (rows ?? []).map(r => r.clone_uo === cloneUo ? { ...r, [key]: val } : r);
      return {
        ...prev,
        double_jump_rows:    prev.double_jump_rows    === null ? null : patch(prev.double_jump_rows),
        triple_jump_rows:    prev.triple_jump_rows    === null ? null : patch(prev.triple_jump_rows),
        quadruple_jump_rows: prev.quadruple_jump_rows === null ? null : patch(prev.quadruple_jump_rows),
      };
    });
  };

  /** Aggiorna un campo TESTUALE di una riga (per clone_uo) nelle 3 tabelle.
   *  Il matcher usa il valore corrente della chiave, così anche l'edit del
   *  clone_uo stesso resta coerente keystroke per keystroke. */
  const updateRowText = (cloneUo: string, key: keyof JumpRow, value: string) => {
    setData(prev => {
      if (!prev) return prev;
      const patch = (rows: JumpRow[] | null) => (rows ?? []).map(r => r.clone_uo === cloneUo ? { ...r, [key]: value } : r);
      return {
        ...prev,
        double_jump_rows:    prev.double_jump_rows    === null ? null : patch(prev.double_jump_rows),
        triple_jump_rows:    prev.triple_jump_rows    === null ? null : patch(prev.triple_jump_rows),
        quadruple_jump_rows: prev.quadruple_jump_rows === null ? null : patch(prev.quadruple_jump_rows),
      };
    });
  };

  /** Aggiunge una riga vuota alla tabella dei salti del tab corrente. */
  const addRow = () => {
    setData(prev => {
      if (!prev) return prev;
      if (tab === 'double')
        return { ...prev, double_jump_rows: [...(prev.double_jump_rows ?? []), emptyJumpRow()] };
      if (tab === 'triple')
        return { ...prev, triple_jump_rows: [...(prev.triple_jump_rows ?? []), emptyJumpRow()] };
      return { ...prev, quadruple_jump_rows: [...(prev.quadruple_jump_rows ?? []), emptyJumpRow()] };
    });
  };

  const rows: JumpRow[] = useMemo(() => {
    if (!data) return [];
    const src = (tab === 'double' ? data.double_jump_rows
              : tab === 'triple' ? data.triple_jump_rows
              : data.quadruple_jump_rows) ?? [];
    return [...src].sort((a, b) => b.fascia_score_q4 - a.fascia_score_q4 || b.final_score_q4 - a.final_score_q4);
  }, [data, tab]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  // Le tre liste sono null insieme (validatore BE): l'analisi non e' stata
  // eseguita (fonte PVR Quota A 30/09 non caricata).
  const notAvailable   = data.double_jump_rows === null;
  const doubleCount    = data.double_jump_rows?.length    ?? 0;
  const tripleCount    = data.triple_jump_rows?.length    ?? 0;
  const quadrupleCount = data.quadruple_jump_rows?.length ?? 0;
  const total          = doubleCount + tripleCount + quadrupleCount;

  const TABS: { key: TabKey; label: string; count: number }[] = [
    { key: 'double',    label: 'Doppio salto',    count: doubleCount },
    { key: 'triple',    label: 'Triplo salto',    count: tripleCount },
    { key: 'quadruple', label: 'Quadruplo salto', count: quadrupleCount },
  ];

  return (
    <div className="p-6 space-y-5">

      {/* Banner Investigation + Edit bar */}
      <div className="flex items-start gap-3">
        <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50/60 border border-amber-100">
          <ArrowUpFromLine className="w-4 h-4 text-amber-700 shrink-0" />
          <p className="text-xs text-amber-900 leading-relaxed">
            <span className="font-semibold">Investigation</span> · Analisi delle filiali che hanno effettuato un salto significativo di fascia (PVR Quota A) tra Q3 e Q4. La ratio sottostante tale investigation risiede nell'individuare possibili condotte commerciali aggressive che la Rete potrebbe attuare al fine di raggiungere una Fascia superiore a cui corrisponde un premio di maggiore entità.
          </p>
        </div>
        <ExportXlsxButton
          fetchBlob={() => fetchExportConductRiskDriverXlsx(reportId, 'Driver10')}
          fileName={`Salto_di_Fasce_${data.period}`}
        />
        <EditActionBar
          isEditing={isEditing}
          onProcedi={() => setIsEditing(true)}
          onAnnulla={handleAnnulla}
          onSalva={handleSalva}
          disabled={isReadOnly}
        />
      </div>

      {notAvailable ? (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50/60 border border-amber-100">
          <ArrowUpFromLine className="w-4 h-4 text-amber-700 shrink-0" />
          <p className="text-xs text-amber-800 leading-relaxed">
            Analisi non disponibile: la fonte PVR Quota A al 30/09 non è stata caricata, quindi il confronto dei salti di fascia tra Q3 e Q4 non è stato eseguito.
          </p>
        </div>
      ) : (
      <>
      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-3xl font-bold text-[#1B365D]">{total}</div>
          <div className="text-[11px] text-gray-400 mt-1">Filiali con salto</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-3xl font-bold text-amber-700">{doubleCount}</div>
          <div className="text-[11px] text-gray-400 mt-1">Doppio salto</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-3xl font-bold text-orange-700">{tripleCount}</div>
          <div className="text-[11px] text-gray-400 mt-1">Triplo salto</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-3xl font-bold text-red-700">{quadrupleCount}</div>
          <div className="text-[11px] text-gray-400 mt-1">Quadruplo (NO&nbsp;PREMIO → I&nbsp;FASCIA)</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
              tab === t.key
                ? 'bg-white text-amber-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
            <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.18 }}
        >
          {rows.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-sm text-gray-400">
              Nessuna filiale in questa categoria.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <p className="text-xs font-semibold text-amber-800">
                  {TABS.find(t => t.key === tab)?.label} — {data.period}
                </p>
                <span className="text-[10px] text-amber-700 font-semibold bg-amber-50 border border-amber-100 px-2.5 py-0.5 rounded-full">
                  {rows.length} filiali
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                      <th className="text-left  px-4 py-2.5 font-semibold">CloneUo</th>
                      <th className="text-left  px-4 py-2.5 font-semibold">Filiale</th>
                      <th className="text-left  px-4 py-2.5 font-semibold">Area</th>
                      <th className="text-left  px-4 py-2.5 font-semibold">Dir. Regionale</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Punt. Q3</th>
                      <th className="text-center px-4 py-2.5 font-semibold">Fascia Q3</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Punt. Q4</th>
                      <th className="text-center px-4 py-2.5 font-semibold">Fascia Q4</th>
                      <th className="text-center px-4 py-2.5 font-semibold">Salto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((row, i) => (
                      <motion.tr
                        key={i}
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.02 }}
                        className="hover:bg-amber-50/30 transition-colors"
                      >
                        <td className="px-4 py-3 font-mono text-[10px] text-gray-500 whitespace-nowrap">
                          {isEditing ? <input type="text" className={txtCls} value={row.clone_uo} onChange={e => updateRowText(row.clone_uo, 'clone_uo', e.target.value)} /> : row.clone_uo}
                        </td>
                        <td className="px-4 py-3 font-medium text-[#1B365D] whitespace-nowrap">
                          {isEditing ? <input type="text" className={txtCls} value={row.branch_name} onChange={e => updateRowText(row.clone_uo, 'branch_name', e.target.value)} /> : row.branch_name}
                        </td>
                        <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                          {isEditing ? <input type="text" className={txtCls} value={row.area} onChange={e => updateRowText(row.clone_uo, 'area', e.target.value)} /> : row.area}
                        </td>
                        <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                          {isEditing ? <input type="text" className={txtCls} value={row.regional_division} onChange={e => updateRowText(row.clone_uo, 'regional_division', e.target.value)} /> : row.regional_division}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600 tabular-nums">
                          {isEditing
                            ? <input type="number" step="0.01" className={numCls} value={row.final_score_q3} onChange={e => updateRow(row.clone_uo, 'final_score_q3', e.target.value)} />
                            : fmtN2(row.final_score_q3)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isEditing ? <input type="text" className={txtCls} value={row.fascia_q3} onChange={e => updateRowText(row.clone_uo, 'fascia_q3', e.target.value)} /> : <FasciaBadge fascia={row.fascia_q3} />}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600 font-semibold tabular-nums">
                          {isEditing
                            ? <input type="number" step="0.01" className={numCls} value={row.final_score_q4} onChange={e => updateRow(row.clone_uo, 'final_score_q4', e.target.value)} />
                            : fmtN2(row.final_score_q4)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isEditing ? <input type="text" className={txtCls} value={row.fascia_q4} onChange={e => updateRowText(row.clone_uo, 'fascia_q4', e.target.value)} /> : <FasciaBadge fascia={row.fascia_q4} />}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isEditing ? <input type="number" step="1" className={numCls} value={row.jump_size} onChange={e => updateRow(row.clone_uo, 'jump_size', e.target.value)} /> : <JumpBadge size={row.jump_size} />}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {isEditing && (
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={() => addRow()}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
          </button>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default SaltoDiFasceContainer;
